/**
 * Calculation method dropdown for bulk default-path / flow-linked optimizers only.
 * Per-path offers & landers use `paths/settings/PathSettingsCalculationMethodSelect`.
 */

import type { FC } from 'react';
import { HelpCircle } from 'lucide-react';
import type { VoluumCalculationMethod } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  CALCULATION_METHOD_LABEL,
  CALCULATION_METHODS,
} from '../../../paths/settings/path-settings.constants';

const DEFAULT_METHOD_TOOLTIP =
  'How Voluum scores default-path weights when this bulk control runs. Updating it writes the same method onto every default path in the draft.';

const DEFAULT_METHOD_ARIA = 'Default path weight optimization method';

export interface CalculationMethodSelectProps {
  /** Null when default paths disagree (reflects API). */
  value: VoluumCalculationMethod | null;
  onChange: (v: VoluumCalculationMethod) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Single horizontal row: label + select (e.g. narrow sidebars). */
  inlineRow?: boolean;
  className?: string;
  /** Override aria-label / tooltip for non-default-path scopes (e.g. rule groups). */
  ariaLabel?: string;
  tooltipText?: string;
}

const SELECT_WIDTH = 'w-[7.25rem] max-w-full';

const CalculationMethodSelect: FC<CalculationMethodSelectProps> = ({
  value,
  onChange,
  disabled = false,
  compact = true,
  inlineRow = false,
  className,
  ariaLabel = DEFAULT_METHOD_ARIA,
  tooltipText = DEFAULT_METHOD_TOOLTIP,
}) => {
  const resolved = value ?? '';
  return (
    <label
      className={cn(
        inlineRow
          ? 'inline-flex min-w-0 items-center gap-1.5'
          : 'flex flex-col gap-1 self-start',
        className,
      )}
    >
      <span
        className={cn(
          'flex items-center text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
          inlineRow ? 'shrink-0 gap-0.5' : 'gap-1',
        )}
      >
        Method
        <Tooltip content={tooltipText}>
          <HelpCircle className="h-3 w-3" aria-label="About method" />
        </Tooltip>
      </span>
      <select
        value={resolved}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value as VoluumCalculationMethod;
          if (next) onChange(next);
        }}
        className={cn(
          SELECT_WIDTH,
          'rounded-md border border-input bg-background shadow-sm focus:outline-none focus:ring-2 focus:ring-ring',
          compact
            ? inlineRow
              ? 'h-7 px-1.5 text-[11px]'
              : 'h-8 px-2 text-xs'
            : 'h-9 px-2 text-sm',
        )}
        aria-label={ariaLabel}
      >
        {value === null ? (
          <option value="" disabled>
            Multiple values (Voluum API)
          </option>
        ) : null}
        {CALCULATION_METHODS.map((m) => (
          <option key={m} value={m}>
            {CALCULATION_METHOD_LABEL[m]}
          </option>
        ))}
      </select>
    </label>
  );
};

export default CalculationMethodSelect;
